import { ButtonProps } from "@mui/material";
import Button from "@mui/material/Button";
import { MdsAddXs } from "@sinch/icons";
import React, { FC, memo, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAddTreeNode } from "../hooks/UseAddTreeNode";
import { MdsIcon } from "@sinch/components";
import { useIsParentWrapper } from "@sinch/components/application/RatelistBuilder/hooks/UseIsParentWrapper";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

// Button for adding an 'else' node to a wrapper
export const AddElseNodeButton: FC<{ parentId: string | null }> = ({ parentId }) => {
  const { t } = useTranslation();
  const { disableConditions } = useRateBuilderSettings();

  const { addCondition } = useAddTreeNode(parentId, disableConditions);
  const { mode } = useRateBuilderSettings();
  const handleAddElse = useCallback(() => {
    addCondition();
  }, [addCondition]);

  return (
    <Button
      disabled={mode === "view" || mode === "override"}
      fullWidth={false}
      onClick={handleAddElse}
      size="small"
      startIcon={<MdsIcon fontSize="xs" icon={MdsAddXs} />}
    >
      {t("RateBuilder.addElseNode")}
    </Button>
  );
};

// Main toolbar component for rate builder items
export const RateBuilderItemToolsBar: FC<{ parentId: string | null; label: ReactNode } & ButtonProps> = memo(
  ({ parentId, label, ...props }) => {
    const { disableConditions } = useRateBuilderSettings();
    const { addCondition } = useAddTreeNode(parentId, disableConditions);
    const { mode } = useRateBuilderSettings();

    // Action handlers
    const handleAddCondition = useCallback(() => {
      addCondition();
    }, [addCondition]);

    // Return specialized button for wrapper nodes
    const isParentWrapper = useIsParentWrapper(parentId);
    if (isParentWrapper) {
      return <AddElseNodeButton parentId={parentId} />;
    }

    // Return dropdown menu for standard nodes
    return (
      <Button
        disabled={mode === "view" || mode === "override"}
        fullWidth={false}
        onClick={handleAddCondition}
        size="small"
        startIcon={<MdsIcon fontSize="xs" icon={MdsAddXs} />}
        {...props}
      >
        {label}
      </Button>
    );
  }
);
RateBuilderItemToolsBar.displayName = "RateBuilderItemToolsBar";
